import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  Paper,
  Typography,
  Container,
  Grid,
  Button,
  Link,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { ApplicationState } from 'store';
import { userActions } from 'actions/userActions';
import { ValidatedTextInput } from 'components/ValidatedTextInput';
import logo from 'static/media/logo2x.png';
import microsoftLogo from 'static/media/microsoft-logo.svg';
import 'translations/translations';
import ButtonSpinner from 'components/spinners/ButtonSpinner';
import {
  entraLoginRequest,
  handleEntraRedirect,
  msalInstance,
} from 'auth/msalConfig';

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: '8%',
  },
  paper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '25rem',
    margin: 'auto',
    padding: '1.8rem 3.125rem',
  },
  img: {
    maxWidth: '18rem',
    marginBottom: '2rem',
  },
  form: {
    width: '100%', // Fix IE 11 issue.
  },
  submit: {
    margin: theme.spacing(6, 0, 1, 0),
    paddingRight: theme.spacing(5),
    paddingLeft: theme.spacing(5),
    color: '#fff',
    fontWeight: 'bold',
  },
  error: {
    color: theme.palette.error.main,
    textAlign: 'right',
    fontSize: '0.8rem',
  },
  alignRight: {
    textAlign: 'right',
  },
  alignCenter: {
    textAlign: 'center',
  },
  divider: {
    textAlign: 'center',
    margin: theme.spacing(2, 0, 1, 0),
    color: theme.palette.text.secondary,
  },
  microsoft: {
    width: '100%',
    textTransform: 'none',
    fontWeight: 'bold',
  },
}));

type LocationProps = {
  referrer?: string;
};

const Login: React.FC = () => {
  const classes = useStyles();
  const error = useSelector(
    (state: ApplicationState) => state.isLoggedIn.error,
  );

  const location = useLocation<LocationProps>();
  const [submitPressed, setSubmitPressed] = useState(false);
  const [isDisabled, setIsDisabeld] = useState(true);

  const [inputs, setInputs] = useState({
    username: '',
    password: '',
  });
  const { username, password } = inputs;
  const dispatch = useDispatch();
  const { t } = useTranslation(['translations']);
  const [entraInProgress, setEntraInProgress] = useState(false);
  const [entraError, setEntraError] = useState(false);

  useEffect(() => {
    handleEntraRedirect()
      .then((result) => {
        if (result?.idToken) {
          setEntraInProgress(true);
          return dispatch(
            userActions.loginWithEntra(
              result.idToken,
              location.state?.referrer,
            ),
          );
        }
      })
      .catch(() => {
        setEntraError(true);
      })
      .finally(() => setEntraInProgress(false));
  }, []);

  const handleEntraLogin = () => {
    setEntraError(false);
    setEntraInProgress(true);
    msalInstance.loginRedirect(entraLoginRequest).catch(() => {
      setEntraInProgress(false);
      setEntraError(true);
    });
  };

  useEffect(() => {
    if (inputs.password && inputs.username) {
      setIsDisabeld(false);
    } else {
      setIsDisabeld(true);
    }
  }, [inputs]);

  const handleChange = (name: string, value: string) => {
    setInputs((inputs) => ({ ...inputs, [name]: value }));
    if (value === '' || !value) {
      setIsDisabeld(true);
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitPressed(true);
    dispatch(userActions.login(username, password, location.state?.referrer));
  };

  const isLoggingIn = submitPressed && !error;

  return (
    <Container className={classes.container}>
      <Paper className={classes.paper}>
        <img src={logo} alt="Logo" className={classes.img} />
        <form className={classes.form} onSubmit={(e) => handleSubmit(e)}>
          <ValidatedTextInput
            type="email"
            required={true}
            label={t('login_page.email')}
            handleChangeCallback={handleChange}
            inputName="username"
            autoFocus={true}
          />

          <ValidatedTextInput
            type="password"
            required={true}
            label={t('login_page.password')}
            handleChangeCallback={handleChange}
            inputName="password"
            autoFocus={false}
          />
          <Typography className={classes.error}>
            {error &&
              error !== 'Microsoft' &&
              error !== 'MicrosoftTenant' &&
              `${error}: ${t('login_page.errors.login_failed')}`}
          </Typography>
          <Grid container>
            <Grid item xs={12} className={classes.alignRight}>
              <Link
                href="/forgot-password"
                variant="body1"
                role="forgotPassLink"
              >
                {t('login_page.forgot_password_link')}
              </Link>
            </Grid>
            <Grid item xs={12} className={classes.alignCenter}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                className={classes.submit}
                disabled={isDisabled || isLoggingIn}
                startIcon={<ButtonSpinner isLoading={isLoggingIn} />}
              >
                {t('login_page.sign_in')}
              </Button>
            </Grid>
          </Grid>
        </form>
        <Typography className={classes.divider}>
          {t('login_page.or')}
        </Typography>
        <Button
          variant="outlined"
          color="primary"
          className={classes.microsoft}
          onClick={handleEntraLogin}
          disabled={entraInProgress}
          startIcon={
            entraInProgress ? (
              <ButtonSpinner isLoading={true} />
            ) : (
              <img src={microsoftLogo} alt="" />
            )
          }
        >
          {t('login_page.sign_in_with_microsoft')}
        </Button>
        {(entraError || error === 'Microsoft') && (
          <Typography className={classes.error}>
            {t('login_page.errors.entra_login_failed')}
          </Typography>
        )}
        {error === 'MicrosoftTenant' && (
          <Typography className={classes.error}>
            {t('login_page.errors.entra_tenant_not_allowed')}
          </Typography>
        )}
      </Paper>
    </Container>
  );
};

export { Login };
